"use client";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CartSchema } from "../types";
import { formatCurrency, useModalState } from "@/lib";
import { InfoTooltip } from "@/components/molecules";
import { PayWithWallet } from "@/app/(procure)/procure/wallet/_components/pay-with-wallet-v2/PayWithWallet";
import { GetAmountBreakdownSchema, WalletSchema } from "../../wallet/types";
import FundWalletModal from "../../wallet/_components/FundWallet";
import CreateWalletModal from "@/app/(procure)/procure/wallet/_components/CreateWallet";
import { usePathname } from "next/navigation";
import { getSourcingRequestFromCart } from "../services";
import { useRouter } from "next/navigation";
import { PaymentEntityType } from "../../wallet/_components/pay-with-wallet-v2/usePayWithWallet";
import { PayWithPaystack } from "../../wallet/_components/PayWithPaystack";
import { Badge } from "@/components";
import { useStoreContext } from "@/lib/store";

export default function CartPayment({
  wallet,
  amountBreakdown,
  cart,
  dataIsLoading,
}: {
  wallet: WalletSchema;
  amountBreakdown: GetAmountBreakdownSchema;
  cart: CartSchema;
  dataIsLoading: boolean;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const {
    closeModal: closeFundModal,
    openModal: openFundModal,
    modalIsOpen: FundModalIsOpen,
  } = useModalState();
  const [isOpen, setIsOpen] = useState(false);
  const [createWalletModalIsOpen, setCreateWalletModalIsOpen] = useState(false);
  const { appSettings } = useStoreContext();

  const onSuccessfulPayment = async () => {
    const sourcingRequest = await getSourcingRequestFromCart(cart?._id ?? "");
    if (sourcingRequest.ok === false) {
      toast.error(sourcingRequest.error?.message ?? "Something went wrong");
    } else {
      toast.loading("Redirecting to request...");
      router.push(`/procure/requests/${sourcingRequest._id}`);
      toast.dismiss();
    }
    return null;
  };

  useEffect(() => {
    setCreateWalletModalIsOpen(false);
    closeFundModal();
  }, [pathname]);

  if (dataIsLoading) {
    return <p>Loading cart...</p>;
  }

  return (
    <div className="flex flex-col pt-2 px-4 bg-white border border-zinc-950/10 rounded-md">
      <p className="text-xl font-bold py-2">Summary</p>
      <div className="divide-y divide-gray-100 space-y-1">
        <div className="flex items-center justify-between py-2">
          <p className="text-sm text-gray-600">Delivery Address</p>
          <p className="text-sm font-medium text-gray-900">
            {cart?.deliveryAddress}
          </p>
        </div>
        <div className="flex items-center justify-between py-2">
          <p className="text-sm text-gray-600">Shipping Method</p>
          <p className="text-sm font-medium text-gray-900">
            {cart?.shipmentMethod}
          </p>
        </div>
        <div className="flex items-center justify-between py-2">
          <p className="text-sm text-gray-600">{"Subtotal"}</p>
          <p className="text-sm font-bold text-gray-900">
            {formatCurrency(
              amountBreakdown?.breakdown?.["subtotal"].amount ?? 0,
            )}
          </p>
        </div>
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center space-x-1">
            <p className="capitalize text-sm text-gray-600">
              {amountBreakdown?.breakdown?.["internalShipping"].title ??
                "Shipping"}
            </p>
            <InfoTooltip
              message="This is an estimated cost. The exact internal shipping fee will be shared when we place your order."
              title="Shipping Cost Information"
              description="About your shipping cost estimate"
            />
          </div>
          <p className="text-sm font-medium text-gray-900">
            {formatCurrency(
              amountBreakdown?.breakdown?.["internalShipping"].amount ?? 0,
            )}
          </p>
        </div>
        <div className="flex items-center justify-between py-2">
          <p className="text-sm text-gray-600">
            {amountBreakdown?.breakdown?.["serviceCharge"].title ??
              "Service Charge"}
          </p>
          <p className="text-sm font-medium text-gray-900">
            {formatCurrency(
              amountBreakdown?.breakdown?.["serviceCharge"].amount ?? 0,
            )}
          </p>
        </div>
        <div className="flex items-center justify-between py-4">
          <p className="text-lg font-bold text-gray-900">Total</p>
          <p className="text-lg font-bold text-gray-900">
            {formatCurrency(amountBreakdown?.amount ?? 0)}
          </p>
        </div>
        <div className="py-2">
          {appSettings.minCartTotal &&
            amountBreakdown.amount < appSettings?.minCartTotal && (
              <Badge color="orange">
                Your cart total must be at least{" "}
                {formatCurrency(appSettings?.minCartTotal)} to proceed to
                checkout.
              </Badge>
            )}
        </div>
      </div>
      <FundWalletModal
        handleOpen={openFundModal}
        handleClose={closeFundModal}
        isOpen={FundModalIsOpen}
        wallet={wallet}
      />
      <CreateWalletModal
        handleClose={() => setCreateWalletModalIsOpen(false)}
        handleOpen={() => setCreateWalletModalIsOpen(true)}
        isOpen={createWalletModalIsOpen}
      />
      <div className="flex items-center space-x-2 pb-3">
        <PayWithWallet
          isOpen={isOpen}
          paymentEntityType={PaymentEntityType.CART}
          entityId={cart?._id ?? ""}
          setIsOpen={setIsOpen}
          button={"Pay with Wallet"}
          onSuccess={onSuccessfulPayment}
          full={true}
          paymentKey={"cart-payment-breakdown"}
          onFailure={(error) => toast.error(error)}
          metadata={{}}
        />
        <PayWithPaystack
          label={""}
          paymentEntityType={PaymentEntityType.CART}
          entityId={cart?._id ?? ""}
          paymentKey={"cart-payment-breakdown"}
          metadata={{}}
          onSuccess={onSuccessfulPayment}
          onFailure={(error) => toast.error(error)}
        />
      </div>
    </div>
  );
}
